import React, { useState, useEffect } from 'react';
import { Lock, Puzzle, Hash, Calculator, Loader2, Unlock, X } from 'lucide-react';
import { Button } from '@/components/common';

// Local type definition
type ChallengeType = 'riddle' | 'hex_code' | 'numerical';

interface ChallengeState {
  pinId: string;
  isActive: boolean;
  lockedByUserId?: string;
  lockedByDisplayName?: string;
  lockedAt?: string;
  isSolved: boolean;
  solvedByUserId?: string;
  solvedByDisplayName?: string;
  solvedAt?: string;
}

interface ChallengeSurpriseRevealProps {
  pinIndex: number;
  challengeType: ChallengeType;
  challengeQuestion: string;
  challengeState: ChallengeState | null;
  currentUserId: string;
  isCaptain: boolean;
  captainName: string;
  onSubmit: (answer: string) => Promise<{ correct: boolean; message: string }>;
  onRelease: () => Promise<void>;
  onClose: () => void;
}

/**
 * Dramatic challenge reveal with surprise lock animation
 * Shows different views for captain (who solves) vs helpers (who assist)
 */
export const ChallengeSurpriseReveal: React.FC<ChallengeSurpriseRevealProps> = ({
  pinIndex,
  challengeType,
  challengeQuestion,
  challengeState,
  currentUserId: _currentUserId, // Reserved for future use
  isCaptain,
  captainName,
  onSubmit,
  onRelease,
  onClose,
}) => {
  const [phase, setPhase] = useState<'reveal' | 'challenge' | 'unlocking'>('reveal');
  const [answer, setAnswer] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isReleasing, setIsReleasing] = useState(false);
  const [feedback, setFeedback] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  const isSolved = challengeState?.isSolved;

  // Auto-transition from reveal to challenge after animation
  useEffect(() => {
    const timer = setTimeout(() => {
      setPhase('challenge');
    }, 2500); // 2.5 second reveal animation
    return () => clearTimeout(timer);
  }, []);

  const getChallengeIcon = () => {
    switch (challengeType) {
      case 'riddle':
        return <Puzzle className="w-16 h-16" />;
      case 'hex_code':
        return <Hash className="w-16 h-16" />;
      case 'numerical':
        return <Calculator className="w-16 h-16" />;
    }
  };

  const getChallengeColor = () => {
    switch (challengeType) {
      case 'riddle':
        return { primary: 'rgb(168, 85, 247)', glow: 'rgba(168, 85, 247, 0.6)' }; // purple
      case 'hex_code':
        return { primary: 'rgb(34, 211, 238)', glow: 'rgba(34, 211, 238, 0.6)' }; // cyan
      case 'numerical':
        return { primary: 'rgb(251, 191, 36)', glow: 'rgba(251, 191, 36, 0.6)' }; // amber
    }
  };

  const getChallengeTypeLabel = () => {
    switch (challengeType) {
      case 'riddle':
        return 'RIDDLE';
      case 'hex_code':
        return 'HEX CODE';
      case 'numerical':
        return 'NUMERICAL';
    }
  };

  const getPlaceholder = () => {
    switch (challengeType) {
      case 'riddle':
        return 'Enter one word answer';
      case 'hex_code':
        return 'Enter 4-character code (e.g., A7F2)';
      case 'numerical':
        return 'Enter number';
    }
  };

  const handleSubmit = async () => {
    if (!answer.trim() || !isCaptain) return;

    setIsSubmitting(true);
    setFeedback(null);

    try {
      const result = await onSubmit(answer.trim());
      if (result.correct) {
        setFeedback({ type: 'success', message: result.message });
        // Show unlock celebration animation
        setPhase('unlocking');
        // Auto close after unlock animation
        setTimeout(() => onClose(), 3000);
      } else {
        setFeedback({ type: 'error', message: result.message });
        setAnswer('');
      }
    } catch {
      setFeedback({ type: 'error', message: 'Failed to submit answer' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRelease = async () => {
    setIsReleasing(true);
    try {
      await onRelease();
    } finally {
      setIsReleasing(false);
    }
  };

  const colors = getChallengeColor();

  // Unlocking phase - celebration animation when challenge solved
  if (phase === 'unlocking') {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center overflow-hidden bg-black">
        {/* Dynamic styles for unlock animation */}
        <style>{`
          @keyframes unlockBurst {
            0% { transform: scale(0); opacity: 1; }
            100% { transform: scale(5); opacity: 0; }
          }
          @keyframes lockToUnlock {
            0% { transform: scale(1) rotate(0deg); }
            20% { transform: scale(1.2) rotate(-10deg); }
            40% { transform: scale(0.9) rotate(5deg); opacity: 1; }
            50% { opacity: 0; }
            51% { opacity: 1; }
            60% { transform: scale(1.3) rotate(-5deg); }
            80% { transform: scale(1.1) rotate(3deg); }
            100% { transform: scale(1) rotate(0deg); }
          }
          @keyframes sparkleFloat {
            0% { transform: translateY(0) scale(1); opacity: 1; }
            100% { transform: translateY(-100px) scale(0); opacity: 0; }
          }
          @keyframes successPulse {
            0%, 100% { transform: scale(1); filter: drop-shadow(0 0 20px rgba(34, 197, 94, 0.6)); }
            50% { transform: scale(1.1); filter: drop-shadow(0 0 40px rgba(34, 197, 94, 0.9)); }
          }
          @keyframes checkmarkDraw {
            0% { stroke-dashoffset: 50; }
            100% { stroke-dashoffset: 0; }
          }
          @keyframes celebrateText {
            0% { opacity: 0; transform: translateY(30px) scale(0.5); }
            50% { transform: translateY(-10px) scale(1.1); }
            100% { opacity: 1; transform: translateY(0) scale(1); }
          }
          @keyframes confettiFall {
            0% { transform: translateY(-100px) rotate(0deg); opacity: 1; }
            100% { transform: translateY(100vh) rotate(720deg); opacity: 0; }
          }
        `}</style>

        {/* Burst rings */}
        {[0, 0.1, 0.2].map((delay, i) => (
          <div
            key={i}
            className="absolute w-32 h-32 rounded-full border-4 border-green-500 pointer-events-none"
            style={{ animation: `unlockBurst 1s ease-out ${delay}s forwards` }}
          />
        ))}

        {/* Confetti particles */}
        {Array.from({ length: 20 }).map((_, i) => (
          <div
            key={`confetti-${i}`}
            className="absolute w-3 h-3 rounded-full"
            style={{
              left: `${10 + Math.random() * 80}%`,
              top: '-20px',
              backgroundColor: ['#22c55e', '#fbbf24', '#3b82f6', '#ec4899', '#8b5cf6'][i % 5],
              animation: `confettiFall ${2 + Math.random() * 2}s ease-out ${Math.random() * 0.5}s forwards`,
            }}
          />
        ))}

        {/* Central content */}
        <div className="relative z-10 flex flex-col items-center text-center px-6">
          {/* Lock to Unlock animation */}
          <div className="relative mb-6">
            {/* Locked state (fades out) */}
            <div
              className="text-gray-500"
              style={{
                animation: 'lockToUnlock 1s cubic-bezier(0.34, 1.56, 0.64, 1) forwards',
              }}
            >
              <Lock className="w-24 h-24" style={{ display: 'none' }} />
            </div>
            {/* Unlocked state (appears) */}
            <div
              className="text-green-500"
              style={{
                animation: 'successPulse 1s ease-in-out infinite',
                filter: 'drop-shadow(0 0 30px rgba(34, 197, 94, 0.8))',
              }}
            >
              <Unlock className="w-24 h-24" />
            </div>

            {/* Sparkles around unlock */}
            {[0, 60, 120, 180, 240, 300].map((angle, i) => (
              <div
                key={angle}
                className="absolute w-2 h-2 bg-yellow-400 rounded-full"
                style={{
                  top: '50%',
                  left: '50%',
                  transform: `rotate(${angle}deg) translateY(-60px)`,
                  animation: `sparkleFloat 1.5s ease-out ${i * 0.1}s infinite`,
                  boxShadow: '0 0 10px rgba(250, 204, 21, 0.8)',
                }}
              />
            ))}
          </div>

          {/* Success checkmark circle */}
          <div className="mb-4">
            <svg width="80" height="80" viewBox="0 0 80 80">
              <circle
                cx="40"
                cy="40"
                r="35"
                fill="none"
                stroke="rgba(34, 197, 94, 0.3)"
                strokeWidth="4"
              />
              <circle
                cx="40"
                cy="40"
                r="35"
                fill="none"
                stroke="#22c55e"
                strokeWidth="4"
                strokeDasharray="220"
                strokeDashoffset="220"
                style={{ animation: 'checkmarkDraw 0.8s ease-out 0.3s forwards' }}
              />
              <path
                d="M25 42 L35 52 L55 28"
                fill="none"
                stroke="#22c55e"
                strokeWidth="5"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeDasharray="50"
                strokeDashoffset="50"
                style={{ animation: 'checkmarkDraw 0.5s ease-out 0.6s forwards' }}
              />
            </svg>
          </div>

          {/* Success text */}
          <div
            style={{
              animation: 'celebrateText 0.6s ease-out 0.4s forwards',
              opacity: 0,
            }}
          >
            <h1 className="text-3xl font-bold text-green-400 mb-2">UNLOCKED!</h1>
            <p className="text-xl text-green-300">Challenge Complete!</p>
            {feedback && (
              <p className="text-gray-400 mt-2">{feedback.message}</p>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Reveal phase - dramatic surprise animation
  if (phase === 'reveal') {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center overflow-hidden">
        {/* Dynamic styles for animations */}
        <style>{`
          @keyframes shockwave {
            0% { transform: scale(0); opacity: 1; }
            100% { transform: scale(4); opacity: 0; }
          }
          @keyframes lockSlam {
            0% { transform: scale(3) rotate(-30deg); opacity: 0; }
            50% { transform: scale(1.2) rotate(10deg); opacity: 1; }
            70% { transform: scale(0.9) rotate(-5deg); }
            100% { transform: scale(1) rotate(0deg); }
          }
          @keyframes iconPulse {
            0%, 100% { transform: scale(1); filter: drop-shadow(0 0 20px ${colors.glow}); }
            50% { transform: scale(1.1); filter: drop-shadow(0 0 40px ${colors.glow}); }
          }
          @keyframes screenShake {
            0%, 100% { transform: translateX(0); }
            10%, 30%, 50%, 70%, 90% { transform: translateX(-5px); }
            20%, 40%, 60%, 80% { transform: translateX(5px); }
          }
          @keyframes flashBang {
            0% { opacity: 0; }
            10% { opacity: 1; }
            100% { opacity: 0; }
          }
          @keyframes textReveal {
            0% { opacity: 0; transform: translateY(20px); }
            100% { opacity: 1; transform: translateY(0); }
          }
        `}</style>

        {/* Flash effect */}
        <div 
          className="absolute inset-0 bg-white pointer-events-none"
          style={{ 
            animation: 'flashBang 0.5s ease-out forwards',
            animationDelay: '0.2s',
          }}
        />

        {/* Dark overlay with shake */}
        <div 
          className="absolute inset-0 bg-black"
          style={{ animation: 'screenShake 0.5s ease-out' }}
        />

        {/* Shockwave rings */}
        <div 
          className="absolute w-32 h-32 rounded-full border-4 pointer-events-none"
          style={{ 
            borderColor: colors.primary,
            animation: 'shockwave 1s ease-out forwards',
            animationDelay: '0.3s',
          }}
        />
        <div 
          className="absolute w-32 h-32 rounded-full border-4 pointer-events-none"
          style={{ 
            borderColor: colors.primary,
            animation: 'shockwave 1s ease-out forwards',
            animationDelay: '0.5s',
          }}
        />
        <div 
          className="absolute w-32 h-32 rounded-full border-4 pointer-events-none"
          style={{ 
            borderColor: colors.primary,
            animation: 'shockwave 1s ease-out forwards',
            animationDelay: '0.7s',
          }}
        />

        {/* Central content */}
        <div className="relative z-10 flex flex-col items-center text-center px-6">
          {/* Giant lock icon slamming in */}
          <div 
            className="mb-6"
            style={{ 
              animation: 'lockSlam 0.8s cubic-bezier(0.34, 1.56, 0.64, 1) forwards',
              color: colors.primary,
            }}
          >
            <Lock className="w-24 h-24" style={{ filter: `drop-shadow(0 0 30px ${colors.glow})` }} />
          </div>

          {/* Challenge type icon pulsing */}
          <div 
            className="mb-4"
            style={{ 
              animation: 'iconPulse 1s ease-in-out infinite',
              animationDelay: '0.8s',
              color: colors.primary,
            }}
          >
            {getChallengeIcon()}
          </div>

          {/* Text reveal */}
          <div 
            className="text-white"
            style={{ 
              animation: 'textReveal 0.5s ease-out forwards',
              animationDelay: '1s',
              opacity: 0,
            }}
          >
            <h1 className="text-3xl font-bold mb-2">CHALLENGE!</h1>
            <p className="text-xl" style={{ color: colors.primary }}>
              {getChallengeTypeLabel()}
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Challenge phase - show challenge for captain or helper message
  return (
    <div className="fixed inset-0 bg-black/90 flex items-center justify-center z-50 p-4 pb-safe-bottom">
      <div className="bg-secondary rounded-2xl w-full max-w-md p-6 animate-slide-up relative max-h-[85vh] overflow-y-auto">
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-text-secondary hover:text-white transition-colors"
          aria-label="Close"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Header with challenge icon */}
        <div className="flex flex-col items-center mb-6">
          <div 
            className="w-16 h-16 rounded-full flex items-center justify-center mb-3"
            style={{ 
              backgroundColor: `${colors.primary}20`,
              color: colors.primary,
            }}
          >
            {getChallengeIcon()}
          </div>
          <h2 className="text-xl font-bold">PIN {pinIndex + 1} CHALLENGE</h2>
          <span 
            className="px-3 py-1 rounded-full text-sm font-medium mt-2 flex items-center gap-1.5"
            style={{ 
              backgroundColor: `${colors.primary}20`,
              color: colors.primary,
            }}
          >
            {challengeType === 'riddle' && <Puzzle className="w-4 h-4" />}
            {challengeType === 'hex_code' && <Hash className="w-4 h-4" />}
            {challengeType === 'numerical' && <Calculator className="w-4 h-4" />}
            {getChallengeTypeLabel()}
          </span>
        </div>

        {/* Solved state */}
        {isSolved && (
          <div className="bg-green-500/20 border border-green-500/50 rounded-lg p-4 mb-4 text-center">
            <div className="text-4xl mb-2">🎉</div>
            <p className="text-green-300 font-medium">Challenge Completed!</p>
            <p className="text-sm text-text-secondary mt-1">
              Solved by {challengeState?.solvedByDisplayName}
            </p>
            <Button variant="primary" onClick={onClose} className="w-full mt-4">
              Continue
            </Button>
          </div>
        )}

        {/* Captain view - can enter answers */}
        {!isSolved && isCaptain && (
          <>
            {/* Captain badge */}
            <div className="flex items-center justify-center gap-2 mb-4 px-4 py-2 bg-primary/20 rounded-lg">
              <span className="text-xl">👑</span>
              <span className="text-primary font-medium">You are the Team Captain!</span>
            </div>

            {/* Question */}
            <div className="bg-background rounded-lg p-4 mb-4">
              <p className="text-text-primary text-center text-lg">{challengeQuestion}</p>
            </div>

            {/* Hint for helpers */}
            <p className="text-sm text-text-secondary text-center mb-4">
              Your teammates can see this challenge. Work together to find the answer!
            </p>

            {/* Answer input */}
            <div className="space-y-4">
              <input
                type={challengeType === 'numerical' ? 'number' : 'text'}
                value={answer}
                onChange={(e) => setAnswer(
                  challengeType === 'hex_code' 
                    ? e.target.value.toUpperCase() 
                    : e.target.value
                )}
                placeholder={getPlaceholder()}
                maxLength={challengeType === 'hex_code' ? 4 : undefined}
                className={`w-full px-4 py-3 rounded-lg bg-background text-text-primary 
                           text-center text-xl placeholder:text-text-muted ${
                  challengeType === 'hex_code' ? 'uppercase font-mono tracking-wider' : ''
                }`}
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleSubmit();
                }}
              />

              {/* Feedback */}
              {feedback && (
                <div className={`p-3 rounded-lg text-center ${
                  feedback.type === 'success' 
                    ? 'bg-green-500/20 text-green-300' 
                    : 'bg-red-500/20 text-red-300'
                }`}>
                  {feedback.message}
                </div>
              )}

              <div className="flex gap-3">
                <Button 
                  variant="secondary" 
                  onClick={handleRelease}
                  disabled={isReleasing || isSubmitting}
                  className="flex-1"
                >
                  {isReleasing ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <>
                      <Unlock className="w-4 h-4 mr-1" />
                      Pass Captain
                    </>
                  )}
                </Button>
                <Button 
                  onClick={handleSubmit}
                  disabled={isSubmitting || !answer.trim()}
                  className="flex-1"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Checking...
                    </>
                  ) : (
                    'SUBMIT'
                  )}
                </Button>
              </div>
            </div>
          </>
        )}

        {/* Helper view - see question but can't answer */}
        {!isSolved && !isCaptain && (
          <>
            {/* Helper badge */}
            <div className="flex items-center justify-center gap-2 mb-4 px-4 py-2 bg-amber-500/20 rounded-lg">
              <span className="text-xl">🤝</span>
              <span className="text-amber-400 font-medium">Help the Captain!</span>
            </div>

            {/* Captain info */}
            <div className="bg-primary/10 border border-primary/30 rounded-lg p-4 mb-4 text-center">
              <p className="text-text-secondary text-sm mb-1">Team Captain</p>
              <p className="text-primary font-bold text-lg flex items-center justify-center gap-2">
                <span>👑</span> {captainName}
              </p>
            </div>

            {/* Question */}
            <div className="bg-background rounded-lg p-4 mb-4">
              <p className="text-text-primary text-center text-lg">{challengeQuestion}</p>
            </div>

            {/* Instructions */}
            <div className="text-center text-text-secondary">
              <p className="mb-2">Work with your team to solve this challenge!</p>
              <p className="text-sm">Only the captain can submit answers.</p>
            </div>

            <Button variant="secondary" onClick={onClose} className="w-full mt-6">
              Close
            </Button>
          </>
        )}
      </div>
    </div>
  );
};

export default ChallengeSurpriseReveal;
